// import node module libraries
import { Fragment } from "react";
import PropTypes from "prop-types";

import { Col, Row, ListGroup, Card, Badge } from "react-bootstrap";
import useAuth from "hooks/useAuth";
import { Link } from "react-router-dom";
import { PATH_DASHBOARD } from "routes/Paths";
import { isAdmin } from "helper/utils";

// ----------------------------------------------------------------------

DescriptionTab.propTypes = {
  course: PropTypes.object,
};

// ----------------------------------------------------------------------

export default function DescriptionTab({ course, isCoursePaid, user }) {
  const requirements = course?.requirements || [];

  const {isAuthenticated}= useAuth();

  const halfIndex = Math.ceil(requirements.length / 2);
  const firstCols = requirements.slice(0, halfIndex);
  const secondCols = requirements.slice(halfIndex);

  const canStart = isAdmin(user?.role?.name);
  const meetDetails = course?.virtualLink2 ? JSON.parse(course?.virtualLink2): null;

  return (
    <Fragment>
      {/* Course Description */}
      <div className="description-section mb-5">
        <div className="section-header mb-4">
          <div className="section-icon">
            <i className="fe fe-book-open"></i>
          </div>
          <div>
            <h3 className="section-title mb-1">About This Course</h3>
            <p className="section-subtitle text-muted mb-0">
              Learn what this course is all about
            </p>
          </div>
        </div>
        <div className="description-content">

           <p
                  className="lead"
                  dangerouslySetInnerHTML={{ __html: course?.description }}
                />
        </div>
      </div>

      {/* Course Requirements */}
      {requirements.length > 0 && (
        <div className="requirements-section mb-5">
          <div className="section-header mb-4">
            <div className="section-icon">
              <i className="fe fe-check-square"></i>
            </div>
            <div>
              <h3 className="section-title mb-1">What You'll Need</h3>
              <p className="section-subtitle text-muted mb-0">
                Requirements to get started
              </p>
            </div>
          </div>

          <Card className="requirements-card">
            <Card.Body className="p-4">
              <Row>
                <Col lg={6} md={6} sm={12}>
                  <ListGroup variant="flush" className="requirements-list">
                    {firstCols.map((item, index) => (
                      <ListGroup.Item key={index} className="requirement-item">
                        <div className="requirement-icon">
                          <i className="fe fe-check-circle"></i>
                        </div>
                        <span className="requirement-text">{item}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Col>
                <Col lg={6} md={6} sm={12}>
                  <ListGroup variant="flush" className="requirements-list">
                    {secondCols.map((item, index) => (
                      <ListGroup.Item key={index} className="requirement-item">
                        <div className="requirement-icon">
                          <i className="fe fe-check-circle"></i>
                        </div>
                        <span className="requirement-text">{item}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Virtual Meeting Link */}
      {((isAuthenticated && isCoursePaid && course?.virtualLink) || (isAuthenticated && canStart)) && (
        <div className="meeting-section">
          <Card className="meeting-card">
            <Card.Body className="p-4">
              <div className="d-flex align-items-start gap-3">
                <div className="meeting-icon">
                  <i className="fe fe-video"></i>
                </div>
                <div className="flex-grow-1">
                  <h4 className="mb-2">
                    <i className="fe fe-calendar me-2"></i>
                    Virtual Classroom
                  </h4>
                  {meetDetails ?
                    <>
                     <p className="text-muted mb-3">
                    Join the live session or start the meeting as an instructor
                  </p>
                  <Link 
                    to={canStart ? meetDetails?.start_url : PATH_DASHBOARD.meeting.join(course?.guid)} 
                    className="btn btn-primary meeting-btn" 
                    target="_blank"
                  >
                    <i className={`fe ${canStart ? 'fe-play-circle' : 'fe-log-in'} me-2`}></i>
                    {canStart ? "Start Meeting" : "Join Meeting"}
                  </Link>
                    </>
                  : 
                  <p className="text-muted mb-3">
                    Link will be available when the course begins
                  </p>}
                 
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      <style jsx>{`
        /* Section Header */
        .section-header {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .section-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border-radius: 12px;
          flex-shrink: 0;
        }

        .section-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .section-subtitle {
          font-size: 0.9375rem;
        }

        /* Description Content */
        .description-content {
          padding-left: 64px;
        }

        .description-content .lead {
          font-size: 1.0625rem;
          line-height: 1.8;
          color: #475569;
        }

        /* Requirements Card */
        .requirements-card {
          border: none;
          background: #f8fafc;
          border-radius: 16px;
          margin-left: 64px;
        }

        .requirements-list {
          background: transparent;
        }

        .requirement-item {
          background: transparent !important;
          border: none !important;
          padding: 1rem 0;
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
        }

        .requirement-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 6px;
          flex-shrink: 0;
        }

        .requirement-icon i {
          font-size: 0.875rem;
          color: #10b981;
        }

        .requirement-text {
          color: #475569;
          font-size: 0.9375rem;
          line-height: 1.6;
          font-weight: 500;
        }

        /* Meeting Card */
        .meeting-card {
          border: none;
          background: linear-gradient(135deg, rgba(246, 133, 33, 0.05) 0%, rgba(29, 158, 176, 0.05) 100%);
          border-radius: 16px;
          border: 2px solid rgba(246, 133, 33, 0.2);
        }

        .meeting-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border-radius: 12px;
          flex-shrink: 0;
        }

        .meeting-icon i {
          font-size: 1.75rem;
          color: white;
        }

        .meeting-card h4 {
          font-weight: 700;
          color: #1e293b;
        }

        .meeting-btn {
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          border-radius: 10px;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border: none;
          transition: all 0.3s ease;
        }

        .meeting-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(246, 133, 33, 0.3);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .description-content,
          .requirements-card {
            padding-left: 0;
            margin-left: 0;
          }

          .section-header {
            flex-direction: column;
          }

          .section-title {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </Fragment>
  );
}